import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, CircularProgress, IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import SaveIcon from '@material-ui/icons/Save';
import RefreshIcon from '@material-ui/icons/Refresh';
import PublishIcon from '@material-ui/icons/Publish';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import GetAppIcon from '@material-ui/icons/GetApp';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import UndoIcon from '@material-ui/icons/Undo';
import RedoIcon from '@material-ui/icons/Redo';
import { undoConfig, redoConfig } from '../api/searchExperienceSlice';
import { useSearchExperience } from '../api/useSearchExperience';
import { useBuildPreview } from '../api/useBuildPreview';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { useBundleRebuild } from '../../home/components/Diagnostics/useBundleRebuild';
import { useShellAppContext } from '../../shell/context/useShellAppContext';
import { ADMIN_RUNTIME_COMMANDS } from '../../../app/runtime/adminRuntime';
import PublishProgressDialog from '../../home/components/PublishProgressDialog';
import { useOperationDialogVisibility } from '../../home/components/PublishProgressDialog/useOperationDialogVisibility';
import SearchFeaturePageShell from '../../../components/shell/SearchFeaturePageShell';
import LayoutEditor from './properties/LayoutEditor';
import EditorOnboarding from './onboarding/EditorOnboarding';
import EditorControlsTheme from './EditorControlsTheme';
import BuildOverlay from './BuildOverlay';
import styles from './SearchExperienceEditor.module.scss';

/** Visual editor with a shared draft, validation and runtime actions. */
const SearchExperienceEditor = ({ client, shopId }) => {
  const t = useLocalizedMessage();
  const dispatch = useDispatch();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const { runtime } = useShellAppContext();
  const [buildOverlayOpen, setBuildOverlayOpen] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [saved, setSaved] = useState(false);
  const submissionRef = useRef(false);
  const {
    config, conflictConfig, isLoading, isLoadingRegistry, isSaving, error, mode,
    currentView, currentScreen, isDirty, validationErrors = [], editorHistory,
    loadConfig, loadSchema, loadRegistry, saveConfig, reset,
    changeScreen, validateNow,
  } = useSearchExperience(client, shopId);
  const { build, building, buildError, buildSuccess, resetBuild } = useBuildPreview(shopId);
  const publishBuild = useBundleRebuild();
  const publishDialog = useOperationDialogVisibility(publishBuild.status);
  const publishPending = ['queued', 'running'].includes(publishBuild.status);
  const busy = Boolean(submission || isSaving || building || publishPending);
  const previewBuildSupported = runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW) === true;
  const productionPublishSupported = runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE) === true;

  const loadEditor = useCallback(async () => {
    setActionError(null);
    try { await Promise.all([loadConfig(mode), loadSchema(), loadRegistry()]); }
    catch (failure) { setActionError(failure.message || t('search.settings.config.loadFailed')); }
  }, [loadConfig, loadSchema, loadRegistry, mode, t]);

  useEffect(() => { if (shopId) loadEditor(); }, [shopId, loadEditor]);
  const screenKeys = Object.keys(config?.search?.views?.[currentView]?.screens || {}).join('|');
  useEffect(() => {
    const screens = screenKeys ? screenKeys.split('|') : [];
    if (screens.length && !screens.includes(currentScreen)) {
      changeScreen(screens.includes('results') ? 'results' : screens[0]);
    }
  }, [screenKeys, currentScreen, changeScreen]);

  const runAction = async (name, action) => {
    if (submissionRef.current || busy) return;
    submissionRef.current = true;
    setSubmission(name);
    setActionError(null);
    setSaved(false);
    try { await action(); }
    catch (failure) { setActionError(failure.message || t('search.settings.config.saveFailed')); }
    finally { submissionRef.current = false; setSubmission(null); }
  };

  const saveDraft = async () => {
    const validation = await validateNow();
    if (!validation?.valid) {
      setActionError(validation?.errors?.[0]?.message || t('search.settings.config.validationFailed'));
      return false;
    }
    const result = await saveConfig();
    if (!result?.success) {
      setActionError(result?.errors?.[0]?.message || result?.error || t('search.settings.config.saveRejected'));
      return false;
    }
    setSaved(true);
    return true;
  };
  const handleSave = () => runAction('save', saveDraft);
  const handleSaveAndPreview = () => runAction('preview', async () => {
    if (!await saveDraft()) return;
    resetBuild();
    setBuildOverlayOpen(true);
    await build();
  });
  const handleBuildRetry = () => runAction('preview', async () => {
    resetBuild();
    setBuildOverlayOpen(true);
    await build();
  });
  const startPublish = async () => {
    publishBuild.reset();
    publishDialog.begin();
    await publishBuild.startBuildAndPromote(shopId);
  };
  const handlePublish = () => {
    if (busy || submissionRef.current || isDirty) return;
    runAction('publish', startPublish);
  };
  const handleReset = () => { reset(); setActionError(null); setSaved(false); };
  const handleExport = (snapshot = config) => {
    if (!snapshot) return;
    const { _editorState, ...exported } = snapshot;
    const blob = new Blob([JSON.stringify(exported, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `search-experience-${shopId}-${mode}-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if ((isLoading || isLoadingRegistry) && !config) {
    return <SearchFeaturePageShell pageId="settingsV2" isLoading />;
  }
  if (!config) {
    return <SearchFeaturePageShell pageId="settingsV2"><Box className={styles.loading}>
      <Alert severity={error || actionError ? 'error' : 'info'}>{error || actionError || t('searchInterface.editor.noConfig')}</Alert>
      <Button onClick={loadEditor} startIcon={<RefreshIcon />}>{t('searchInterface.editor.retry')}</Button>
    </Box></SearchFeaturePageShell>;
  }

  const disabled = busy || isLoading || isLoadingRegistry;
  return (
    <EditorControlsTheme><div className={styles.root} data-testid="search-experience-editor" data-layout="visual" onKeyDown={event => {
      if (!(event.metaKey || event.ctrlKey) || disabled || event.target.closest('input, textarea, [contenteditable="true"]')) return;
      if (event.key.toLowerCase() === 'z') { event.preventDefault(); dispatch(event.shiftKey ? redoConfig() : undoConfig()); }
      if (event.key.toLowerCase() === 's') { event.preventDefault(); if (isDirty) handleSave(); }
    }}>
      <SearchFeaturePageShell
        pageId="settingsV2"
        description={isDirty ? t('experience.design.unsaved') : saved ? t('searchInterface.workspace.saved') : ''}
        actions={(
          <div className={styles.actions}>
            <Tooltip title={t('experience.design.undo')}><span><IconButton onClick={() => dispatch(undoConfig())} disabled={disabled || !editorHistory?.past?.length} aria-label={t('experience.design.undo')}><UndoIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title={t('experience.design.redo')}><span><IconButton onClick={() => dispatch(redoConfig())} disabled={disabled || !editorHistory?.future?.length} aria-label={t('experience.design.redo')}><RedoIcon fontSize="small" /></IconButton></span></Tooltip>
            {previewBuildSupported && <Button variant="outlined" onClick={handleSaveAndPreview} disabled={disabled} startIcon={<PlayArrowIcon />} data-testid="search-experience-save-preview">{t('experience.design.testInStore')}</Button>}
            <Button variant="contained" color="primary" onClick={handleSave} disabled={!isDirty || disabled} startIcon={submission === 'save' ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} data-testid="search-experience-save">{t(isSaving ? 'searchInterface.editor.saving' : 'searchInterface.workspace.saveDraft')}</Button>
            <Tooltip title={t('experience.design.moreActions')}><IconButton onClick={event => setMenuAnchor(event.currentTarget)} aria-label={t('experience.design.moreActions')} aria-haspopup="menu"><MoreHorizIcon /></IconButton></Tooltip>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => { setMenuAnchor(null); handleExport(); }} disabled={disabled} data-testid="search-experience-export"><GetAppIcon fontSize="small" />&nbsp;{t('searchInterface.toolbar.export')}</MenuItem>
              <MenuItem onClick={() => { setMenuAnchor(null); handleReset(); }} disabled={!isDirty || disabled} data-testid="search-experience-reset"><RefreshIcon fontSize="small" />&nbsp;{t('experience.design.discardChanges')}</MenuItem>
              {mode === 'preview' && productionPublishSupported && <MenuItem onClick={() => { setMenuAnchor(null); handlePublish(); }} disabled={isDirty || disabled || Boolean(conflictConfig)} data-testid="search-experience-publish"><PublishIcon fontSize="small" />&nbsp;{t('searchInterface.toolbar.publish')}</MenuItem>}
            </Menu>
          </div>
        )}
      >
        {(actionError || error) && <Alert severity="error" className={styles.alert}>{actionError || error}</Alert>}
        {conflictConfig && <Alert severity="warning" className={styles.alert}>
          <Button onClick={() => handleExport()}>{t('experience.design.exportDraft')}</Button>
          <Button onClick={() => handleExport(conflictConfig)}>{t('experience.design.exportCurrent')}</Button>
          <Button onClick={loadEditor}>{t('experience.design.reloadCurrent')}</Button>
        </Alert>}
        {validationErrors.length > 0 && <Alert severity="error" className={styles.alert}>
          {t('searchInterface.editor.validationErrors')}
          <ul>{validationErrors.map((item, index) => <li key={item.path || item.field || index}>{item.path || item.field}: {item.message}</li>)}</ul>
        </Alert>}
        <div className={styles.content} aria-busy={disabled}>
          <LayoutEditor client={client} shopId={shopId} disabled={disabled} previewActions={<EditorOnboarding shopId={shopId} disabled={disabled} />} />
        </div>
        {previewBuildSupported && <BuildOverlay open={buildOverlayOpen} state={building ? 'building' : buildSuccess ? 'success' : buildError ? 'error' : 'building'} error={buildError} onClose={() => { setBuildOverlayOpen(false); resetBuild(); }} onRetry={handleBuildRetry} />}
        {productionPublishSupported && <PublishProgressDialog open={publishDialog.open} operation="production" status={publishBuild.status} error={publishBuild.error} onRetry={() => runAction('publish', startPublish)} onClose={publishDialog.close} localizeMessage={t} />}
      </SearchFeaturePageShell>
    </div></EditorControlsTheme>
  );
};
export default SearchExperienceEditor;
